import React, { useState } from 'react';
import './AboutPage.css';

const AboutPage = () => {
  const [videoStates, setVideoStates] = useState({
    overview: false,
    character: false,
    questionVectors: false,
    personalization: false,
    digitalConcierge: false,
    prototype: false,
    whatNext: false
  });

  const toggleVideo = (videoKey) => {
    setVideoStates(() => ({
      overview: false,
      character: false,
      questionVectors: false,
      personalization: false,
      digitalConcierge: false,
      prototype: false,
      whatNext: false,
      [videoKey]: true
    }));
  };

  const VideoPlaceholder = ({ videoKey, title, embedUrl }) => {
    if (videoStates[videoKey]) {
      const autoplayUrl = embedUrl.includes('?')
        ? `${embedUrl}&autoplay=1`
        : `${embedUrl}?autoplay=1`;

      return (
        <iframe
          key={videoKey}
          src={autoplayUrl}
          frameBorder="0"
          allowFullScreen
          className="loom-video"
          title={title}
          allow="autoplay; fullscreen"
        ></iframe>
      );
    }
    
    return (
      <div className="video-placeholder" onClick={() => toggleVideo(videoKey)}>
        <div className="play-icon">▶</div>
        <div className="video-label">{title}</div>
      </div>
    );
  };
  return (
    <div className="about-page">
      <div className="about-content">
        {/* Jeeves Junior Overview Section */}
        <div className="about-section">
          <div className="left-column">
            <img 
              src="/images/jj_illus_character_large.png" 
              alt="Jeeves Junior Character" 
              className="character-image"
            />
            <div className="video-container">
              <VideoPlaceholder 
                videoKey="overview"
                title="Jeeves Junior Overview"
                embedUrl="https://www.loom.com/embed/0d010b8837724e5f8fcf05c95fd32569?sid=2c28b2c1-23ee-4ce6-90a7-ee3352b43415"
              />
            </div>
          </div>
          <div className="about-text">
            <h1 className="about-title">About Jeeves Junior</h1>
            <h2 className="section-title">Jeeves Junior Overview</h2>
            <p className="about-description">
              Jeeves Junior started with the proposition that it would – given severe resource restraints – use the latest chatbot AI technology but enhance it with some elements that made Ask Jeeves distinctive. This includes elements that were in-progress or still "futures" when I left Ask Jeeves. In particular:
            </p>
            <ul className="about-list">
              <li>Jeeves Junior is a character-centric interface.</li>
              <li>Jeeves Junior reinterprets Ask Jeeves "questions it knows how to answer" in the modern context with "subject prompts".</li>
              <li>Jeeves Junior includes personalization, so he learns about you with use to serve you better over time. (This was under development when I left Ask Jeeves.)</li>
              <li>Given new technology, Jeeves Junior should be able to fulfill the "digital concierge" role and perform actions for its users, which was part of Ask Jeeves original goals.</li>
            </ul>
            <p className="about-description">
              I will explain how each of these has been, or will be, achieved.
            </p>
          </div>
        </div>

        {/* The Character Section */}
        <div className="about-section">
          <div className="about-text">
            <h2 className="section-title">The Character</h2>
            <p className="about-description">
              Several characters were considered before choosing Jeeves Junior, and not all of them were referential to Ask Jeeves. However, since the initial self-assigned charter was to reinvent "Ask Jeeves using today's AI technology", I selected the cartoonish Jeeves Junior character. The Jeeves Junior character was created and drawn by Amy Warthen, my youngest daughter, who is a senior at Cal Poly Pomona majoring in Graphic Design. She also designed the website and other images.
            </p>
            <p className="about-description">
              The other aspect is choosing how to refer to the character. We have explored using Jeeves Junior, Jeeves Jr., Junior, and JJ. You will see these nicknames used on the site. This is not sloppy design. I am experimenting with the different nicknames, with the goal of eventually setting clear "name rules" in the yet-to-be-written Jeeves Junior Style Guide.  Both web and mobile app frontends have been created.
            </p>
          </div>
          <div className="video-container">
            <VideoPlaceholder 
              videoKey="character"
              title="The Character Development"
              embedUrl="https://www.loom.com/embed/d40e44aa11cc45e9ac01f211726fbb66?sid=ccd858d2-6b2a-483a-92ad-60f0524d0a84"
            />
          </div>
        </div>

        {/* Character Designs Section */}
        <div className="about-section character-designs-section">
          <div className="about-text">
            <p className="about-description">
              Other character designs explored included:
            </p>
            <div className="character-designs">
              <div className="design-group">
                <div className="design-images">
                  <img 
                    src="/images/jj_about_realistic_image1.jpg" 
                    alt="Less cartoonish Jeeves Junior 1" 
                    className="design-image"
                  />
                  <img 
                    src="/images/jj_about_realistic_image2.jpg" 
                    alt="Less cartoonish Jeeves Junior 2" 
                    className="design-image"
                  />
                </div>
                <p className="design-caption">A less cartoonish Jeeves Junior</p>
              </div>
              
              <div className="design-group">
                <img 
                  src="/images/jj_about_jiles_image.jpg" 
                  alt="Jiles - a hip, diverse Jeeves" 
                  className="design-image"
                />
                <p className="design-caption">Jiles (a hip, diverse Jeeves)</p>
              </div>
              
              <div className="design-group">
                <img 
                  src="/images/jj_about_the_professor_image.png" 
                  alt="The Professor" 
                  className="design-image"
                />
                <p className="design-caption">The Professor</p>
              </div>
            </div>
          </div>
        </div>

        {/* Updating Question Vectors Section */}
        <div className="about-section">
          <div className="video-container">
            <VideoPlaceholder 
              videoKey="questionVectors"
              title="Updating Question Vectors"
              embedUrl="https://www.loom.com/embed/50adae55613a48749c161e475791152a"
            />
          </div>
          <div className="about-text">
            <h2 className="section-title">Updating Question Vectors</h2>
            <p className="about-description">
              Ask Jeeves core functionality was centered around "questions Jeeves knew how to answer", which it answered either (a) with a link taking the user to a page that answered the question (question vectors), or (b) or directly displaying the answer for a few "cherry picked" cases (direct answers). In terms of the "long tail curve" of questions/queries, Ask Jeeves was a solution for the "big head" backstopped by our metasearch partners providing the "long tail" solution. The combination provided a powerful total solution for the user.
            </p>
            <p className="about-description">
              To update this concept for Jeeves Junior, when the discussion with the user is detected to be on a subject that Jeeves Junior "knows about", then the system inserts a "<strong>subject prompt</strong>" into the AI's instructions to become an expert on that subject. Jeeves Junior maintains a library of these <strong>subject prompts</strong> that can be expanded based on which subjects users most commonly chat about – the "big head" of the conversation curve.
            </p>
          </div>
        </div>

        {/* Personalization Section */}
        <div className="about-section">
          <div className="about-text">
            <h2 className="section-title">Personalization</h2>
            <p className="about-description">
              The last project I was working on at Ask Jeeves was to have the system learn about the user with usage. Initially, every day was like Jeeves' "first day on the job". The idea was to accumulate data about the user to tailor Jeeves' responses based on this data and even to anticipate the user's questions. This was an early foray into data science and big data, before those terms existed or their concepts were clear. For Jeeves Junior, personalization by learning about the user from usage is an initial feature, as the underlying chatbot AI itself pretrained and does not learn.
            </p>
          </div>
          <div className="video-container">
            <VideoPlaceholder 
              videoKey="personalization"
              title="Personalization Feature"
              embedUrl="https://www.loom.com/embed/869067b5d5ff40f58c746d93e72d4934?sid=eeb4692d-e045-42f9-802e-16aff6fa53ae"
            />
          </div>
        </div>

        {/* Digital Concierge Section */}
        <div className="about-section">
          <div className="video-container">
            <VideoPlaceholder 
              videoKey="digitalConcierge"
              title="Digital Concierge Vision"
              embedUrl="https://www.loom.com/embed/99b4da2c7e6b4d69bddb9abd52086e82"
            />
          </div>
          <div className="about-text">
            <h2 className="section-title">Digital Concierge</h2>
            <p className="about-description">
              Ask Jeeves original vision was to answer users' questions and to do things for the user. This latter concept came to be referred to as the Jeeves "Digital Concierge" functionality. We never got to develop this, beyond some simplistic partner tie-ins to the Jeeves Product Recommender. As this is a powerful part of the vision that really makes the service complete, I have included it in my Jeeves Junior gameplan. Both the internet and certain aspects of recent AI functionality make this much more achievable today than it was in the Ask Jeeves era. It also fits nicely into the Jeeves Junior Subject Prompt framework described above.
            </p>
          </div>
        </div>

        {/* Prototype, not Product Section */}
        <div className="about-section">
          <div className="about-text">
            <h2 className="section-title">Prototype, not Product</h2>
            <p className="about-description">
              Given the limited resources available to me, basically my time and incurring as little cost as possible for cloud infrastructure, I set limits on my Jeeves Junior objectives. Some of the objectives are:
            </p>
            <ul className="about-list">
              <li>No foundational model development (way too expensive)</li>
              <li>Limited model fine-tuning options (for various reasons)</li>
              <li>Limited manpower, basically the time I can spare to work on it</li>
            </ul>
            <p className="about-description">
              This still leaves considerable room for innovation by using prompts, personalization, agents, and the UI.
            </p>
            <p className="about-description">
              Even with these constraints, each of the major features selected can become a time sink of experimentation, refinement, and iteration. So, I am limiting myself to developing a "first version" of each feature to demonstrate it, while avoiding the time sink of refining it… Otherwise I will wind up with only part of Jeeves Junior, not a functioning system. Then, these first versions can be refined later, as time and resources permit.
            </p>
            <p className="about-description">
              However, this does not mean that Jeeves Junior is throwaway code. I am building professional, well-designed and documented software, following standards, with robustness and scalability in mind. I am limiting effort on tasks that do not demonstrate any innovation, like security, which is implemented at a basic level. These would need to be revisited and "beefed up" to transition from the Jeeves Junior prototype to a Minimum Viable Product.
            </p>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0', flex: '0 0 400px', maxWidth: '400px' }}>
            <div className="video-container">
              <VideoPlaceholder 
                videoKey="prototype"
                title="Prototype Development Approach"
                embedUrl="https://www.loom.com/embed/918eaece6fec424594e811c59e43e6c8"
              />
            </div>
            <div className="video-container" style={{ marginTop: '-2.75rem' }}>
              <VideoPlaceholder 
                videoKey="whatNext"
                title="What Next?"
                embedUrl="https://www.loom.com/embed/93739453eb64415e85da47fdabce0caa"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AboutPage;
